import Big from 'big.js';
import { DateTime } from 'luxon';
import { AxiosError } from 'smg/axios';
import * as smgCarts from 'smg/customer-api/v2/carts';
import { CreateContact } from 'smg/customer-api/contact';
import { ChildMap } from 'smg/customer-api/lineitems';
import * as models from 'smg/customer-api/models';
import { Session } from 'smg/customer-api/session';
import { ShowError } from 'smg/errors';
import * as filepath from 'smg/filepath';
import * as phone from 'smg/phone';
import QueryString from 'smg/querystring';
import * as strings from 'smg/strings';
import * as template from 'smg/template';
import { LoadAll } from 'smg/uploads';
import { DollarFormat, ParsePositiveInt } from 'smg/util';
import * as carts from '../carts';
import { Relref } from '../hugo';
import * as lineitems from '../lineitems';

declare interface myWindow extends Window {
    readonly cloudFront: string;
}
declare const window: myWindow;

type cartLineItem = models.QuoteLineItem | models.OrderLineItem;

interface lineNotice {
    tone: 'success' | 'warning' | 'light';
    text: string;
    // A reload action stays usable while the cart is stale; every other
    // action is locked with the rest of the cart.
    action?: { label: string; run: () => void; reload?: boolean };
}

// A removed cart item leaves an undoable notice where it stood; nextCartItemID
// is the item that followed it (undefined when it was last), so the notice
// keeps its place across redraws.
interface removedCartItem {
    cartItemID: string;
    nextCartItemID?: string;
    kind: string;
}

// What the cart is doing. Every action control reads from this: while a line
// is being edited or a change is in flight, the rest of the cart waits, and a
// change whose refresh failed leaves the cart stale until a reload.
type cartInteraction =
    { kind: 'idle' } |
    { kind: 'editing'; lineKey: string } |
    { kind: 'mutating' } |
    { kind: 'stale' };

const reorderMoldDescription = /^(partial )?reorder of (\d+)/i;

// The request form's intro for a quantity under the published minimum; a
// line with no tiers has no minimum to name.
const lowQuantityIntro = (minimum: number): string => {
    if (minimum > 0) {
        return `Our sales team will send a custom quote for this small order under ${Intl.NumberFormat().format(minimum)}. The quantity you typed is filled in below.`;
    }
    return 'Our sales team will send a custom quote for this small order. The quantity you typed is filled in below.';
};

const actionControls = [
    '.cart-item-edit-quantity',
    '.cart-item-quantity-edit',
    '.cart-item-line-delete',
    '.cart-item-delete',
    '.request-low-qty-pricing',
    '.cart-item-removed-undo',
    '.cart-line-notice-action',
].join(', ');

void main();

async function main() {
    const errorId = document.querySelector('#error-div') as HTMLElement;

    const quoteID = QueryString().get('quote');
    if (quoteID) {
        await carts.AddCartItem({ quoteID }, {
            promptToCheckout: false,
            hideCartLink: true,
        }).catch(() => errorId.classList.remove('d-none'));
    }
    const orderID = QueryString().get('order');
    if (orderID) {
        await carts.AddCartItem({ orderID }, {
            promptToCheckout: false,
            hideCartLink: true,
        }).catch(() => errorId.classList.remove('d-none'));
    }
    const newCartItem = QueryString().get('new');
    if (newCartItem) {
        try {
            await carts.AddCartItem(JSON.parse(newCartItem) as smgCarts.AddCartItemParams, { promptToCheckout: false });
        } catch (e) {
            ShowError(e);
        }
    }
    const cartID = QueryString().get('cart');
    if (cartID) {
        await carts.AddCartItem({ cartID }, {
            promptToCheckout: false,
            hideCartLink: true,
            showToast: true,
        }).catch(() => errorId.classList.remove('d-none'));
    }

    const cartCard = document.querySelector('#cart-card') as HTMLElement;

    const spinner = document.querySelector('#cart-spinner') as HTMLElement;
    const checkoutButton = document.querySelector('#checkout-button') as HTMLAnchorElement;
    const checkoutBlockedNote = document.querySelector('#checkout-blocked-note') as HTMLElement;

    const cartEmpty = document.querySelector('#cart-empty') as HTMLElement;
    cartEmpty.parentElement?.removeChild(cartEmpty);
    cartEmpty.classList.remove('d-none');

    const cartItemTemplate = document.querySelector('#cart-item-template') as HTMLTemplateElement;
    const cartItemStockItemTemplate = document.querySelector('#cart-item-stock-item-template') as HTMLTemplateElement;
    // Every repaint empties the card, templates included, so they are held
    // here from the start rather than looked up by selector each time.
    const cartItemRemovedTemplate = document.querySelector('#cart-item-removed-template') as HTMLTemplateElement;
    const lineNoticeTemplate = document.querySelector('#cart-line-notice-template') as HTMLTemplateElement;
    const priceRequestTemplate = document.querySelector('#cart-line-price-request-template') as HTMLTemplateElement;

    let interaction: cartInteraction = { kind: 'idle' };
    // From the last paint: a line is at a quantity the site cannot price, or
    // there is nothing to check out.
    let unpricedLine = false;
    let cartIsEmpty = true;

    // Notices to draw on a line (or a stock item) at the next paint: saved,
    // restored, request sent.
    const pendingLineNotices = new Map<string, lineNotice>();
    // Removed lines, by cart item, drawn as undoable notices under the item's lines.
    const removedLines = new Map<string, Map<string, lineNotice>>();
    const removedCartItems: removedCartItem[] = [];

    const lineKey = (cartItem: models.CartItem, lineItem: cartLineItem) => `${cartItem.id || ''}:${lineItem.id || ''}`;

    // applyInteraction is the one place the cart's controls read the state:
    // every action locks while anything is in flight, and checkout is an
    // anchor, so blocking it means the keyboard too.
    const applyInteraction = () => {
        const locked = interaction.kind !== 'idle';
        cartCard.querySelectorAll<HTMLButtonElement>(actionControls).forEach($el => $el.disabled = locked);

        const blocked = locked || unpricedLine || cartIsEmpty;
        checkoutButton.toggleAttribute('disabled', blocked);
        checkoutButton.setAttribute('aria-disabled', blocked ? 'true' : 'false');
        checkoutButton.tabIndex = blocked ? -1 : 0;
        checkoutBlockedNote.classList.toggle('d-none', !unpricedLine);
    };
    checkoutButton.addEventListener('click', e => {
        if (checkoutButton.hasAttribute('disabled')) {
            e.preventDefault();
        }
    });

    const setInteraction = (next: cartInteraction) => {
        interaction = next;
        applyInteraction();
    };

    const showEmptyCart = () => {
        cartCard.innerHTML = '';
        cartCard.appendChild(cartEmpty);
        cartIsEmpty = true;
        unpricedLine = false;
        applyInteraction();

        const quoteSearchBtn = document.querySelector('#quote-search-btn') as HTMLAnchorElement;
        quoteSearchBtn.classList.add('btn', 'btn-primary', 'text-white');
        quoteSearchBtn.parentElement?.classList.remove('justify-content-start');
        quoteSearchBtn.parentElement?.classList.add('justify-content-center');
        quoteSearchBtn.innerHTML = '<i class="fas fa-search"></i> Search for your quotes.';
    };

    const drawNotice = ($mount: HTMLElement, notice: lineNotice) => {
        const $notice = template.Clone(lineNoticeTemplate).firstElementChild as HTMLElement;
        $notice.classList.add(`alert-${notice.tone}`);
        if (notice.tone === 'light') {
            $notice.classList.add('border');
        }
        ($notice.querySelector('.cart-line-notice-text') as HTMLElement).textContent = notice.text;
        const $action = $notice.querySelector('.cart-line-notice-action') as HTMLButtonElement;
        if (notice.action) {
            $action.textContent = notice.action.label;
            $action.classList.remove('d-none');
            if (notice.action.reload) {
                $action.classList.replace('cart-line-notice-action', 'cart-stale-reload');
            }
            const run = notice.action.run;
            $action.addEventListener('click', () => run());
        }
        $mount.replaceChildren($notice);
        applyInteraction();
        return $notice;
    };

    // enterStale is where a change that reached the server but whose cart
    // refresh failed ends up: prices dim, everything locks, and a reload is
    // the only way on.
    const enterStale = ($mount: HTMLElement | undefined, text: string) => {
        setInteraction({ kind: 'stale' });
        cartCard.querySelectorAll('.cart-item-line-price, .cart-item-subtotal').forEach($el => $el.classList.add('opacity-50'));
        document.querySelectorAll('.cart-subtotal').forEach($el => $el.classList.add('opacity-50'));
        let $slot = $mount;
        if (!$slot) {
            $slot = document.createElement('div');
            $slot.className = 'px-7 pt-7';
            cartCard.prepend($slot);
        }
        drawNotice($slot, { tone: 'warning', text, action: { label: 'Reload', run: () => location.reload(), reload: true } });
    };

    // mutate runs one change against the cart and then refreshes it. The cart
    // is locked meanwhile; a refresh that fails leaves it stale.
    const mutate = async (run: () => Promise<void>, stale: { $mount?: HTMLElement; text: string } = { text: 'Reload the page to see the updated totals.' }) => {
        setInteraction({ kind: 'mutating' });
        try {
            await run();
        } catch (err) {
            setInteraction({ kind: 'idle' });
            ShowError(err);
            return;
        }
        if (await drawCart()) {
            setInteraction({ kind: 'idle' });
        } else {
            enterStale(stale.$mount, stale.text);
        }
    };

    const lineName = (lineItem: cartLineItem, quantity: number): string => {
        let proofName = '';
        if (lineItem instanceof models.QuoteLineItem && lineItem.quoteProof?.number) {
            proofName = `Proof ${lineItem.quoteProof.number}`;
        } else if (lineItem instanceof models.OrderLineItem) {
            proofName = lineItem.orderProof?.name || '';
        }
        const product = (quantity === 1 ? lineItem.lineItemModel?.name : lineItem.lineItemModel?.pluralName) || lineItem.lineItem;
        const name = `${Intl.NumberFormat().format(quantity)} ${product || ''}`;
        if (proofName) {
            return `${proofName} - ${name}`;
        }
        return name;
    };

    // Children that follow the line's quantity ride in the per-unit price; the
    // rest (a mold fee) are charged once.
    const lineTotals = (lineItem: cartLineItem): { total: Big; unit: Big } => {
        const qty = lineItem.quantity || 0;
        let total = Big(lineItem.price || 0).mul(qty);
        let fixed = Big(0);
        const children: (models.QuoteChildLineItem | models.OrderChildLineItem)[] = lineItem instanceof models.QuoteLineItem ?
            lineItem.quoteChildLineItems :
            lineItem.orderChildLineItems;
        for (const child of children) {
            const childTotal = Big(child.price || 0).mul(child.quantity || 0);
            total = total.add(childTotal);
            if (!child.autoQuantity && !child.linkedQuantity) {
                fixed = fixed.add(childTotal);
            }
        }
        const unit = qty > 0 ? total.sub(fixed).div(qty) : Big(0);
        return { total, unit };
    };

    // The reorder rebate rewrites a rebated mold child's description to name
    // the order whose mold is on file. Otherwise the tiers' fixed amount is a
    // mold fee only when every fixed-quantity child on the line is a mold child;
    // any other fixed charge gets no mold wording.
    const lineMold = async (lineItem: models.OrderLineItem): Promise<carts.QuantityEditorMold | undefined> => {
        for (const child of lineItem.orderChildLineItems) {
            const match = reorderMoldDescription.exec(child.description || '');
            if (match && Big(child.price || 0).eq(0)) {
                return { state: 'onFile', order: parseInt(match[2], 10) };
            }
        }
        if (!lineItem.quantityTiers.some(tier => Big(tier.fixedPrice || 0).gt(0))) {
            return undefined;
        }
        const childMap = await ChildMap();
        const fixedChildren = lineItem.orderChildLineItems.filter(child => !child.autoQuantity && !child.linkedQuantity);
        if (fixedChildren.length && fixedChildren.every(child => childMap.get(child.childLineItemID || '')?.mold)) {
            return { state: 'fee' };
        }
        return undefined;
    };

    const drawRemovedCartItem = (removed: removedCartItem): HTMLElement => {
        const $removed = template.Clone(cartItemRemovedTemplate).firstElementChild as HTMLElement;
        ($removed.querySelector('.cart-item-removed-kind') as HTMLElement).textContent = removed.kind;
        const $undo = $removed.querySelector('.cart-item-removed-undo') as HTMLButtonElement;
        $undo.addEventListener('click', () => {
            if (interaction.kind !== 'idle') {
                return;
            }
            void mutate(async () => {
                const restored = await smgCarts.RestoreCartItem(removed.cartItemID);
                carts.SetCartCount(restored.cartItemsCount);
                removedCartItems.splice(removedCartItems.indexOf(removed), 1);
            });
        });
        return $removed;
    };

    const removeCartItem = (cartItem: models.CartItem, $cartItem: HTMLElement) => {
        if (interaction.kind !== 'idle') {
            return;
        }
        let kind = 'item';
        if (cartItem.quote) {
            kind = 'quote';
        } else if (cartItem.order) {
            kind = 'reorder';
        }
        let $next = $cartItem.nextElementSibling;
        if ($next?.nodeName.toLowerCase() === 'hr') {
            $next = $next.nextElementSibling;
        }
        const nextCartItemID = ($next as HTMLElement | null)?.dataset.cartItemId;

        void mutate(async () => {
            const removedResp = await smgCarts.DeleteCartItem(cartItem.id || '');
            carts.SetCartCount(removedResp.cartItemsCount);
            const removed: removedCartItem = { cartItemID: cartItem.id || '', nextCartItemID, kind };
            removedCartItems.push(removed);
            // The notice takes the item's place now, so Undo is there even if
            // the refresh that follows fails.
            $cartItem.replaceWith(drawRemovedCartItem(removed));
            applyInteraction();
        });
    };

    const drawCartItem = (cartItem: models.CartItem): { $: HTMLElement; invalidCustomQuantity: boolean } => {
        const cartItemEl = template.Clone(cartItem.stockItem ? cartItemStockItemTemplate : cartItemTemplate).firstElementChild as HTMLElement;
        cartItemEl.removeAttribute('id');
        cartItemEl.dataset.cartItemId = cartItem.id || '';

        const title = cartItemEl.querySelector('.cart-item-title') as HTMLElement;
        const kind = cartItemEl.querySelector('.cart-item-kind') as HTMLElement;

        const subtotal = cartItemEl.querySelector('.cart-item-subtotal') as HTMLElement;
        subtotal.textContent = DollarFormat(cartItem.total || 0);

        const oldSubtotal = cartItemEl.querySelector('.cart-item-old-subtotal') as HTMLElement;
        if (cartItem.maxNetworkSubtotal && Big(cartItem.maxNetworkSubtotal).gt(cartItem.subtotal || 0)) {
            subtotal.classList.add('text-primary');
            oldSubtotal.textContent = DollarFormat(cartItem.maxNetworkSubtotal);
        } else {
            oldSubtotal.parentElement?.removeChild(oldSubtotal);
        }

        const deleteButton = cartItemEl.querySelector('.cart-item-delete') as HTMLButtonElement;
        deleteButton.addEventListener('click', () => removeCartItem(cartItem, cartItemEl));

        const revisionParent = cartItemEl.querySelector<HTMLElement>('.cart-item-revision-parent');
        const revision = revisionParent?.querySelector<HTMLAnchorElement>('.cart-item-revision');
        if (cartItem.quote?.id && revision) {
            revision.href = `${Relref('/revisions')}?id=${cartItem.quote.key || ''}`;
        } else if (revisionParent) {
            revisionParent.parentElement?.removeChild(revisionParent);
        }

        const itemNotice = cartItemEl.querySelector<HTMLElement>('.cart-item-notice');
        const pendingItemNotice = pendingLineNotices.get(cartItem.id || '');
        if (itemNotice && pendingItemNotice) {
            pendingLineNotices.delete(cartItem.id || '');
            drawNotice(itemNotice, pendingItemNotice);
        }

        const breakdownParent = cartItemEl.querySelector<HTMLElement>('.cart-item-breakdown-parent');
        const breakdownToggle = breakdownParent?.querySelector<HTMLButtonElement>('.cart-item-info');
        const breakdown = breakdownParent?.querySelector<HTMLElement>('.cart-item-breakdown');
        const breakdownLineItems = cartItem.quote?.quoteLineItems || cartItem.order?.orderLineItems || [];
        if (breakdownParent && breakdownToggle && breakdown && breakdownLineItems.length) {
            // Built on first open; a build that fails is forgotten so the next
            // click tries again instead of opening an empty disclosure.
            let building: Promise<void> | undefined;
            breakdownToggle.addEventListener('click', async () => {
                if (!building) {
                    breakdownToggle.setAttribute('aria-busy', 'true');
                    building = lineitems.Table(breakdownLineItems).then($table => {
                        breakdown.replaceChildren($table);
                    }).catch((err: unknown) => {
                        building = undefined;
                        ShowError(err);
                        throw err;
                    }).finally(() => breakdownToggle.removeAttribute('aria-busy'));
                }
                try {
                    await building;
                } catch {
                    return;
                }
                const open = breakdown.classList.toggle('show');
                breakdownToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
            });
        } else {
            breakdownParent?.parentElement?.removeChild(breakdownParent);
        }

        const drawCartItemLine = (lineItem: cartLineItem): HTMLElement => {
            const lineItemEl = template.Clone('#cart-item-line', cartItemEl).firstElementChild as HTMLElement;
            const key = lineKey(cartItem, lineItem);
            const summary = lineItemEl.querySelector('.cart-item-summary') as HTMLElement;

            const proofLink = lineItemEl.querySelector('.proof-link') as HTMLAnchorElement;
            const proofImg = proofLink.querySelector('img') as HTMLImageElement;
            const proof = lineItem instanceof models.QuoteLineItem ? lineItem.quoteProof?.proof : lineItem.orderProof?.proof;
            if (proof?.main) {
                proofLink.href = proof.main;
                proofLink.setAttribute('data-fancybox', `cart-item-${cartItem.id || ''}`);

                proofImg.src = proof.thumbnail || proof.main;

                const proofName = lineName(lineItem, lineItem.quantity || 0).split(' - ')[0];
                proofLink.setAttribute('data-caption', proofName);
                proofImg.title = proofName;
                proofImg.alt = proofName;
            } else {
                proofLink.parentElement?.parentElement?.removeChild(proofLink.parentElement);
            }

            const qty = lineItem.quantity || 0;
            summary.textContent = lineName(lineItem, qty);

            const spec = lineItemEl.querySelector('.cart-item-spec') as HTMLElement;
            const specParts = [lineItem.lineItemType?.name, lineItem.lineItemTypeSize?.name].filter(part => part);
            if (specParts.length) {
                spec.textContent = specParts.join(' · ');
                spec.classList.remove('d-none');
            }

            const price = lineItemEl.querySelector('.cart-item-line-price') as HTMLElement;
            const priceTotal = lineItemEl.querySelector('.cart-item-line-total') as HTMLElement;
            const priceUnit = lineItemEl.querySelector('.cart-item-line-unit') as HTMLElement;
            const priceUnitAmount = lineItemEl.querySelector('.cart-item-line-unit-price') as HTMLElement;
            if (lineItem.lineItemModel?.product) {
                price.classList.remove('d-none');
                if (lineItem.invalidCustomQuantity) {
                    priceTotal.textContent = '—';
                    priceUnit.classList.add('d-none');
                } else {
                    const totals = lineTotals(lineItem);
                    priceTotal.textContent = DollarFormat(totals.total);
                    priceUnitAmount.textContent = DollarFormat(totals.unit);
                }
            }

            const notice = lineItemEl.querySelector('.cart-item-line-notice') as HTMLElement;
            const editorMount = lineItemEl.querySelector('.cart-item-line-editor') as HTMLElement;
            const controls = lineItemEl.querySelector('.cart-item-line-controls') as HTMLElement;
            const changeQty = controls.querySelector('.cart-item-edit-quantity') as HTMLButtonElement;
            const removeParent = controls.querySelector('.cart-item-line-delete-parent') as HTMLElement;
            const remove = removeParent.querySelector('.cart-item-line-delete') as HTMLButtonElement;

            const pending = pendingLineNotices.get(lineItem.id || '');
            if (pending) {
                pendingLineNotices.delete(lineItem.id || '');
                drawNotice(notice, pending);
            }

            // A customer-editable order line can be removed; it can be repriced
            // in place only when the network published tiers for it.
            const canRemove = lineItem instanceof models.OrderLineItem && lineItem.customerEditable === true;
            const tiers = lineItem instanceof models.OrderLineItem ? lineItem.quantityTiers : [];
            const canReprice = canRemove && tiers.length > 0;
            const minimum = tiers.length ? Math.min(...tiers.map(tier => tier.quantity)) : 0;

            const closeEditor = () => {
                editorMount.replaceChildren();
                controls.classList.remove('d-none');
                changeQty.setAttribute('aria-expanded', 'false');
                setInteraction({ kind: 'idle' });
            };

            const openInLine = () => {
                controls.classList.add('d-none');
                changeQty.setAttribute('aria-expanded', 'true');
                setInteraction({ kind: 'editing', lineKey: key });
            };

            const openRequestForm = (intro: string, requestedQty?: number) => {
                if (interaction.kind !== 'idle') {
                    return;
                }
                openInLine();
                mountPriceRequestForm(editorMount, {
                    template: priceRequestTemplate,
                    lineName: lineName(lineItem, qty),
                    intro,
                    quantity: requestedQty,
                    message: `Qty update request for ${cartItem.quote ? 'quote' : 'order'} #${cartItem.quote ? cartItem.quote.serial || '' : cartItem.order?.invoiceNumber || ''}`,
                    lineIndex: (cartItem.quote?.quoteLineItems || cartItem.order?.orderLineItems || []).indexOf(lineItem as never) + 1,
                    onSent: () => {
                        closeEditor();
                        drawNotice(notice, { tone: 'success', text: 'We received your request and the cart is unchanged. Our team will reply with a price.' });
                    },
                    onCancel: closeEditor,
                });
            };

            if (!lineItem.lineItemModel?.product) {
                controls.parentElement?.removeChild(controls);
            } else {
                if (productCount < 2 || !canRemove) {
                    removeParent.parentElement?.removeChild(removeParent);
                }

                if (canReprice) {
                    const sameProductLines = (cartItem.order?.orderLineItems || []).filter(other => other.lineItemID === lineItem.lineItemID && other.lineItemModel?.product).length;
                    changeQty.addEventListener('click', async () => {
                        if (interaction.kind !== 'idle') {
                            return;
                        }
                        openInLine();
                        let mold: carts.QuantityEditorMold | undefined;
                        try {
                            mold = await lineMold(lineItem);
                        } catch (err) {
                            closeEditor();
                            ShowError(err);
                            return;
                        }
                        const oldQty = lineItem.quantity || 1;
                        const editor = carts.MountQuantityEditor(editorMount, {
                            product: lineItem.lineItemModel?.pluralName || lineItem.lineItem || '',
                            tiersInit: tiers,
                            currentQty: oldQty,
                            mold,
                            groupNote: sameProductLines > 1,
                            onCancel: closeEditor,
                            onRequest: requestedQty => {
                                closeEditor();
                                openRequestForm(lowQuantityIntro(minimum), requestedQty);
                            },
                            onSave: async newQty => {
                                if (newQty === oldQty) {
                                    closeEditor();
                                    return;
                                }
                                // A failed PATCH leaves the editor open in its failed state.
                                await smgCarts.UpdateCartItems([{ id: cartItem.id || '', lineItems: [{ id: lineItem.id || '', quantity: newQty }] }]);
                                // Below the minimum the line comes back unpriced, and
                                // its own notice says what is needed; a success line
                                // claiming the totals include it would be wrong.
                                if (newQty >= minimum) {
                                    pendingLineNotices.set(lineItem.id || '', {
                                        tone: 'success',
                                        text: `This line is now ${Intl.NumberFormat().format(newQty)}, and the cart totals include its price.`,
                                    });
                                }
                                editorMount.replaceChildren();
                                summary.textContent = lineName(lineItem, newQty);
                                await mutate(() => Promise.resolve(), {
                                    $mount: notice,
                                    text: 'The new quantity is saved. Reload the page to see the updated totals.',
                                });
                            },
                        });
                        editor.focus();
                    });
                } else {
                    changeQty.addEventListener('click', () => {
                        openRequestForm('This price was set by a member of our team, so a new quantity needs a price from them. Your cart stays as it is until they send the new price.');
                    });
                }

                remove.addEventListener('click', () => {
                    if (interaction.kind !== 'idle') {
                        return;
                    }
                    const lineID = lineItem.id || '';
                    const itemRemoved = removedLines.get(cartItem.id || '') ?? new Map<string, lineNotice>();
                    removedLines.set(cartItem.id || '', itemRemoved);
                    const removedNotice: lineNotice = {
                        tone: 'light',
                        text: 'This line was removed from the reorder.',
                        action: {
                            label: 'Undo',
                            run: () => {
                                if (interaction.kind !== 'idle') {
                                    return;
                                }
                                void mutate(async () => {
                                    await smgCarts.UpdateCartItems([{ id: cartItem.id || '', lineItems: [{ id: lineID, active: true }] }]);
                                    itemRemoved.delete(lineID);
                                    pendingLineNotices.set(lineID, { tone: 'success', text: 'This line is back in the reorder, with the same artwork and quantity.' });
                                });
                            },
                        },
                    };
                    void mutate(async () => {
                        await smgCarts.UpdateCartItems([{ id: cartItem.id || '', lineItems: [{ id: lineID, active: false }] }]);
                        itemRemoved.set(lineID, removedNotice);
                        // The notice takes the line's place now, so Undo is there
                        // even if the refresh that follows fails.
                        const $slot = document.createElement('div');
                        lineItemEl.replaceWith($slot);
                        drawNotice($slot, removedNotice);
                    });
                });
            }

            if (lineItem.invalidCustomQuantity) {
                const invalid = lineItemEl.querySelector('.cart-item-invalid-quantity') as HTMLElement;
                invalid.classList.remove('d-none');
                const invalidText = invalid.querySelector('.cart-item-invalid-quantity-text') as HTMLElement;
                if (minimum > 0) {
                    invalidText.textContent = `This is a small order under ${Intl.NumberFormat().format(minimum)}, so our sales team will send a custom quote. ` +
                        'Checkout waits until you raise the quantity or they have priced it.';
                } else {
                    invalidText.textContent = 'This is a small order. Our sales team will send a custom quote, and checkout waits until you raise the quantity or they have priced it.';
                }

                const lowQtyRequest = invalid.querySelector('.request-low-qty-pricing') as HTMLButtonElement;
                lowQtyRequest.addEventListener('click', () => {
                    openRequestForm(lowQuantityIntro(minimum), qty);
                });
            }

            return lineItemEl;
        };

        const cartItemDescription = cartItemEl.querySelector('.cart-item-description') as HTMLElement;

        const proofs: models.Proof[] = [];
        const shownProofs = new Set<string>();

        let productCount = 0;
        const objectLineItems = cartItem.quote?.quoteLineItems || cartItem.order?.orderLineItems;
        if (objectLineItems) {
            for (const objectLineItem of objectLineItems) {
                if (objectLineItem.lineItemModel?.tax || objectLineItem.lineItemModel?.shipping) {
                    continue;
                }

                productCount++;

                if (productCount >= 2) {
                    break;
                }
            }
        }

        let invalidCustomQuantity = false;

        if (cartItem.quote) {
            title.textContent = `Quote ${cartItem.quote.serial || ''}`;
            kind.textContent = 'Quote';
            kind.classList.replace('text-bg-primary', 'text-bg-secondary');
            if (cartItem.order) {
                const $reorderOf = document.createElement('span');
                $reorderOf.className = 'small text-muted ms-2';
                $reorderOf.textContent = `Reorder of ${cartItem.order.invoiceNumber}`;
                title.after($reorderOf);
            }
            deleteButton.textContent = 'Remove this quote';

            if (cartItem.quote.quoteProofs) {
                for (const quoteProof of cartItem.quote.quoteProofs) {
                    if (!quoteProof.proof) {
                        continue;
                    }
                    quoteProof.proof.name = `Proof ${quoteProof.name || ''}`;
                    proofs.push(quoteProof.proof);
                }
            }

            if (cartItem.quote.quoteLineItems) {
                for (const quoteLineItem of cartItem.quote.quoteLineItems) {
                    if (quoteLineItem.lineItemModel?.tax || quoteLineItem.lineItemModel?.shipping) {
                        continue;
                    }

                    cartItemDescription.appendChild(drawCartItemLine(quoteLineItem));

                    if (quoteLineItem.quoteProof?.proof?.id) {
                        shownProofs.add(quoteLineItem.quoteProof?.proof.id);
                    }

                    if (quoteLineItem.invalidCustomQuantity) {
                        invalidCustomQuantity = true;
                    }
                }
            }
        } else if (cartItem.order) {
            title.textContent = `Reorder of Order ${cartItem.order.invoiceNumber || ''}`;
            kind.textContent = 'Reorder';
            if (cartItem.order.created.isValid) {
                (cartItemEl.querySelector('.cart-item-placed-date') as HTMLElement).textContent = cartItem.order.created.toLocaleString(DateTime.DATE_FULL);
                cartItemEl.querySelector('.cart-item-placed')?.classList.remove('d-none');
            }

            if (cartItem.order.orderProofs) {
                for (const orderProof of cartItem.order.orderProofs) {
                    if (!orderProof.proof) {
                        continue;
                    }
                    orderProof.proof.name = `Proof ${strings.TrimSuffix(orderProof.name || '', filepath.Ext(orderProof.name || ''))}`;
                    proofs.push(orderProof.proof);
                }
            }

            if (cartItem.order.orderLineItems) {
                for (const orderLineItem of cartItem.order.orderLineItems) {
                    if (orderLineItem.lineItemModel?.tax || orderLineItem.lineItemModel?.shipping) {
                        continue;
                    }

                    cartItemDescription.appendChild(drawCartItemLine(orderLineItem));

                    if (orderLineItem.orderProof?.proof?.id) {
                        shownProofs.add(orderLineItem.orderProof?.proof.id);
                    }

                    if (orderLineItem.invalidCustomQuantity) {
                        invalidCustomQuantity = true;
                    }
                }
            }
        } else if (cartItem.stockItem) {
            title.textContent = cartItem.stockItem.name || '';

            if (cartItem.stockItem.stockItemImage) {
                const cartItemImage = cartItemEl.querySelector<HTMLElement>('.cart-item-image');
                if (cartItemImage) {
                    cartItemImage.classList.remove('d-none');
                    const a = cartItemImage.querySelector('a');
                    if (a) {
                        a.href = cartItem.stockItem.stockItemImage.main || '#';
                        a.setAttribute('data-fancybox', `cart-item-${cartItem.id || ''}`);
                        a.setAttribute('data-caption', cartItem.stockItem.name || '');
                    }
                    const img = cartItemImage.querySelector('img');
                    if (img) {
                        img.src = cartItem.stockItem.stockItemImage.thumbnail || '';
                        img.alt = cartItem.stockItem.name || '';
                        img.title = cartItem.stockItem.name || '';
                    }
                }
            }

            if (cartItem.customizations.size) {
                for (const [key, value] of cartItem.customizations) {
                    const customization = template.Clone('.cart-item-customization-template', cartItemEl).firstElementChild as HTMLElement;
                    const keyEl = customization.querySelector<HTMLElement>('.cart-item-customization-key');
                    if (keyEl) {
                        keyEl.textContent = key;
                    }

                    const valueEl = customization.querySelector<HTMLElement>('.cart-item-customization-value');
                    if (valueEl) {
                        valueEl.textContent = value;
                    }

                    cartItemDescription.appendChild(customization);
                }
            }

            const qtyContainer = cartItemEl.querySelector<HTMLElement>('.cart-item-quantity-container');

            const qty = qtyContainer?.querySelector<HTMLElement>('.cart-item-quantity');
            if (qty) {
                qty.textContent = new Intl.NumberFormat().format(cartItem.quantity || 0);
            }

            const qtyEdit = qtyContainer?.querySelector<HTMLButtonElement>('.cart-item-quantity-edit');
            qtyEdit?.addEventListener('click', async () => {
                if (interaction.kind !== 'idle') {
                    return;
                }

                const oldQty = cartItem.quantity || 1;
                const quantityPrices = cartItem.stockItem?.quantityPricing.size ?
                    Object.fromEntries([...cartItem.stockItem.quantityPricing.entries()].map(([q, price]) => [String(q), price.toString()])) :
                    { '1': cartItem.stockItem?.price?.toString() || '0' };
                const newQty = await carts.PromptQuantityEdit(oldQty || 1, quantityPrices);

                await mutate(async () => {
                    const changed = await smgCarts.UpdateCartItems([{ id: cartItem.id || '', quantity: newQty }]);
                    carts.SetCartCount(changed.cartItemsCount);
                    pendingLineNotices.set(cartItem.id || '', {
                        tone: 'success',
                        text: `This item is now ${Intl.NumberFormat().format(newQty)}, and the cart totals include its price.`,
                    });
                }, { $mount: itemNotice ?? undefined, text: 'The new quantity is saved. Reload the page to see the updated totals.' });
            });
        }

        const removedForItem = removedLines.get(cartItem.id || '');
        const removedMount = cartItemEl.querySelector<HTMLElement>('.cart-item-removed-lines');
        if (removedForItem && removedMount) {
            for (const removedNotice of removedForItem.values()) {
                const $slot = document.createElement('div');
                removedMount.appendChild($slot);
                drawNotice($slot, removedNotice);
            }
        }

        const footerProofs = cartItemEl.querySelector('.cart-item-footer-proofs') as HTMLElement;
        let footerHasProofs = false;

        for (const proof of proofs) {
            if (proof.id && shownProofs.has(proof.id)) {
                continue;
            }

            const proofLink = template.Clone('.cart-item-footer-proof', cartItemEl).firstElementChild as HTMLAnchorElement;
            const proofImg = proofLink.querySelector('img') as HTMLImageElement;
            if (proof?.main) {
                proofLink.href = proof.main;
                proofLink.setAttribute('data-fancybox', `cart-item-${cartItem.id || ''}`);
                proofLink.setAttribute('data-caption', proof.name || '');

                proofImg.src = proof.thumbnail || proof.main;

                proofImg.title = proof.name || '';
                proofImg.alt = proof.name || '';
            } else {
                proofLink.parentElement?.removeChild(proofLink);
            }

            footerProofs.appendChild(proofLink);
            footerHasProofs = true;
        }

        if (cartItem.cartItemUploads?.length) {
            const uploadEls: HTMLElement[] = [];
            for (const cartItemUpload of cartItem.cartItemUploads) {
                const upload = cartItemUpload.upload;
                if (!upload) {
                    continue;
                }

                const a = template.Clone('.cart-item-footer-proof', cartItemEl).firstElementChild as HTMLAnchorElement;
                a.href = `${window.cloudFront}/${upload.key || ''}`;
                a.setAttribute('data-fancybox', `cart-item-${cartItem.id || ''}`);
                a.setAttribute('data-caption', upload.name || '');

                uploadEls.push(a);

                footerProofs.appendChild(a);
                footerHasProofs = true;
            }

            LoadAll(uploadEls);
        }

        if (!footerHasProofs) {
            footerProofs.parentElement?.removeChild(footerProofs);
        }

        return { $: cartItemEl, invalidCustomQuantity };
    };

    // Only the most recent redraw may paint: an earlier GetCart that resolves
    // after a newer one started would repaint stale quantities and totals.
    let drawGeneration = 0;

    // Resolves true once the cart on screen matches the server; false when the
    // refresh failed (the error is already shown) and the totals may be stale.
    const drawCart = async (): Promise<boolean> => {
        const generation = ++drawGeneration;
        const subtotal = document.querySelectorAll('.cart-subtotal');
        const prepaymentRows = document.querySelectorAll('.cart-prepayment-row');
        const prepaymentEls = document.querySelectorAll('.cart-prepayment');
        const availableDiscountRows = document.querySelectorAll('.cart-available-discount-row');
        const availableDiscountEls = document.querySelectorAll('.cart-available-discount');

        if (!smgCarts.GetCartID()) {
            showEmptyCart();
            return true;
        }

        return carts.GetCart('cartItems,totals').then(cart => {
            if (generation !== drawGeneration) {
                return true;
            }
            // The server nets any project deposit credit out of total, so reading it
            // straight into "Subtotal" would silently shrink it with nothing explaining
            // the gap. Add the credit back so Subtotal reads pre-prepayment and surface
            // the credit as its own negative line; full deposit detail lives on checkout.
            const prepayment = cart?.totals.prepayment ?? Big(0);
            const availableDiscount = cart?.totals.availableDiscount ?? Big(0);
            // total is net of the available-discount spend too, so add it back for the pre-credit subtotal.
            subtotal.forEach(el => el.textContent = DollarFormat((cart?.totals.total ?? Big(0)).add(prepayment).sub(availableDiscount)));
            if (prepayment.gt(0)) {
                prepaymentRows.forEach(el => el.classList.remove('d-none'));
                prepaymentEls.forEach(el => el.textContent = `−${DollarFormat(prepayment)}`);
            } else {
                // drawCart re-runs after every cart mutation; re-hide the row if a
                // removed item dropped the deposit so it can't show a stale credit.
                prepaymentRows.forEach(el => el.classList.add('d-none'));
            }
            if (availableDiscount.eq(0)) {
                availableDiscountRows.forEach(el => el.classList.add('d-none'));
            } else {
                availableDiscountEls.forEach(el => el.textContent = `−${DollarFormat(availableDiscount.abs())}`);
                availableDiscountRows.forEach(el => el.classList.remove('d-none'));
            }

            const cartItems = cart?.cartItems ?? [];
            if (!cartItems.length && !removedCartItems.length) {
                showEmptyCart();
                return true;
            }

            spinner.parentElement?.removeChild(spinner);

            const frag = document.createDocumentFragment();

            let i = 0;
            // Checkout is gated on the whole cart, so it is decided here from
            // every item and applied with the paint, never by a stale redraw.
            let anyUnpriced = false;
            const append = ($el: HTMLElement) => {
                if (i++) {
                    const hr = document.createElement('hr') as HTMLElement;
                    hr.classList.add('m-0');
                    frag.appendChild(hr);
                }
                frag.appendChild($el);
            };
            for (const cartItem of cartItems) {
                for (const removed of removedCartItems) {
                    if (removed.nextCartItemID === cartItem.id) {
                        append(drawRemovedCartItem(removed));
                    }
                }
                const { $, invalidCustomQuantity } = drawCartItem(cartItem);
                append($);
                if (invalidCustomQuantity) {
                    anyUnpriced = true;
                }
            }
            for (const removed of removedCartItems) {
                if (!removed.nextCartItemID || !cartItems.some(cartItem => cartItem.id === removed.nextCartItemID)) {
                    append(drawRemovedCartItem(removed));
                }
            }

            if (generation !== drawGeneration) {
                return true;
            }
            cartCard.innerHTML = '';
            cartCard.appendChild(frag);
            unpricedLine = anyUnpriced;
            cartIsEmpty = !cartItems.length;
            applyInteraction();
            return true;
        }).catch((err: unknown) => {
            if (generation !== drawGeneration) {
                return true;
            }
            if ((err as AxiosError).response?.status === 404) {
                showEmptyCart();
                return true;
            }
            ShowError(err);
            return false;
        });
    };

    void drawCart();
}

interface priceRequestFormOptions {
    template: HTMLTemplateElement;
    lineName: string;
    intro: string;
    quantity?: number;
    message: string;
    lineIndex: number;
    onSent: () => void;
    onCancel: () => void;
}

// mountPriceRequestForm renders the request-to-sales form under a line the
// site cannot reprice. Sending creates a sales request naming the order and
// the line; nothing in the cart changes.
function mountPriceRequestForm($mount: HTMLElement, opts: priceRequestFormOptions) {
    const $form = template.Clone(opts.template).firstElementChild as HTMLElement;
    ($form.querySelector('.price-request-line') as HTMLElement).textContent = opts.lineName;
    ($form.querySelector('.price-request-intro') as HTMLElement).textContent = opts.intro;

    const $fullName = $form.querySelector('#modal-full-name') as HTMLInputElement;
    const $email = $form.querySelector('#modal-email') as HTMLInputElement;
    const $phone = $form.querySelector('#modal-phone') as HTMLInputElement;
    const $quantity = $form.querySelector('#modal-quantity') as HTMLInputElement;
    const $message = $form.querySelector('#modal-message') as HTMLTextAreaElement;
    const $reCaptchaPlaceholder = $form.querySelector('#modal-recaptcha') as HTMLElement;
    const $send = $form.querySelector('.price-request-send') as HTMLButtonElement;
    const $spinner = $form.querySelector('.price-request-spinner') as HTMLElement;
    const $cancel = $form.querySelector('.price-request-cancel') as HTMLButtonElement;
    const $failed = $form.querySelector('.price-request-failed') as HTMLElement;

    if (opts.quantity) {
        $quantity.value = String(opts.quantity);
    }

    const phoneInput = new phone.Input($phone);
    const fields = [$fullName, $email, $phone, $quantity];
    let sending = false;

    // The phone widget validates asynchronously, so eligibility waits for it
    // rather than reading a validity the widget has not set yet.
    const complete = async (): Promise<boolean> => {
        const phoneOK = await phoneInput.validate().catch(() => false);
        return phoneOK && fields.every($input => $input.value.trim() && $input.checkValidity()) && ParsePositiveInt($quantity.value) !== null;
    };
    const updateSend = async () => {
        const ok = await complete();
        $send.disabled = sending || !ok;
    };
    for (const $input of fields) {
        $input.addEventListener('input', () => void updateSend());
    }
    $phone.addEventListener('countrychange', () => void updateSend());

    void Session().then(sess => {
        if (!$fullName.value) {
            $fullName.value = sess.user.fullName();
        }
        if (!$email.value) {
            $email.value = sess.user.emailAddress.email;
        }
        if (!$phone.value) {
            $phone.value = sess.user.phoneNumber.phone;
            // A programmatic value never fires input, and the phone widget
            // validates on input.
            $phone.dispatchEvent(new Event('input'));
        }
        void updateSend();
    }).catch(() => undefined);

    $send.addEventListener('click', async () => {
        if (sending) {
            return;
        }
        const ok = await complete();
        if (!ok) {
            const invalid = fields.find($input => !$input.value.trim() || !$input.checkValidity());
            invalid?.reportValidity();
            return;
        }
        sending = true;
        $failed.classList.add('d-none');
        $form.querySelectorAll<HTMLInputElement | HTMLButtonElement>('input, textarea, button').forEach($el => $el.disabled = true);
        $spinner.classList.remove('d-none');
        try {
            await CreateContact({
                name: $fullName.value,
                company: '',
                phone: phoneInput,
                email: $email.value,
                content: `${opts.message}

New quantity requested: ${$quantity.value}
For line item #${opts.lineIndex}

Other comments:
${$message.value.trim() || 'None'}`,
                ignoreSMSPreference: true,
                $reCaptchaPlaceholder,
            });
        } catch (err) {
            sending = false;
            $form.querySelectorAll<HTMLInputElement | HTMLButtonElement>('input, textarea, button').forEach($el => $el.disabled = false);
            $spinner.classList.add('d-none');
            $failed.textContent = 'The request did not go through. Please try again.';
            $failed.classList.remove('d-none');
            void updateSend();
            ShowError(err);
            return;
        }
        opts.onSent();
    });
    $cancel.addEventListener('click', () => opts.onCancel());

    $mount.replaceChildren($form);
    void updateSend();
    $quantity.focus();
}
